/**
 * Lender Registration API Service
 */

import { apiClient, handleApiError } from './client';
import type { LenderRegistrationFormData } from '@/constants/lenderRegistration';

export interface LenderRegistrationResponse {
  success: boolean;
  message: string;
  uid: string;
  data: {
    email: string;
    companyName: string;
  };
}

/**
 * Maps frontend form data to the backend DTO format
 */
function toRegistrationPayload(formData: LenderRegistrationFormData, _language: string) {
  // IMPORTANT: keys must match the NestJS DTO (LenderRegistrationDto).
  // The backend uses ValidationPipe with whitelist + forbidNonWhitelisted,
  // so any unknown field triggers a 400. Also, optional string fields are
  // typed `@IsString()`; sending `null` is rejected — use `''` instead.
  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    password: formData.password,
    companyName: formData.companyName,
    address: formData.address,
    phoneNumber: formData.phoneNumber,

    // Lending criteria
    lenderTypes: formData.lenderTypes ?? [],
    loanProductTypes: formData.loanProductTypes ?? [],
    collateralOptions: formData.collateralOptions ?? [],
    minRange: formData.minRange || '',
    maxRange: formData.maxRange || '',
    minGrossRevenue: formData.minGrossRevenue || '',
    maxGrossRevenue: formData.maxGrossRevenue || '',
    minEbitda: formData.minEbitda || '',
    maxEbitda: formData.maxEbitda || '',
    minCashFlow: formData.minCashFlow || '',
    maxCashFlow: formData.maxCashFlow || '',
    debtServiceCoverageRatio: formData.debtServiceCoverageRatio || '',

    // Credit
    creditScoreRequired: formData.creditScoreRequired || '',
    foreignCreditScoreAccepted: formData.foreignCreditScoreAccepted || '',
    foreignCollateralAccepted: formData.foreignCollateralAccepted || '',
    foreignCollateralOptions: formData.foreignCollateralOptions ?? [],

    // Jurisdictions — DTO uses camelCase, frontend stored snake_case
    jurisdictionCanada: formData.jurisdiction_canada ?? [],
    jurisdictionUsa: formData.jurisdiction_usa ?? [],
    jurisdictionEurope: formData.jurisdiction_europe ?? [],
    jurisdictionOthers: formData.jurisdiction_others ?? [],

    // Compliance
    agreeTerms: !!formData.agreeTerms,
    backgroundCheckAuth: !!formData.backgroundCheckAuth,
  };
}

export const lenderApi = {
  /**
   * Register a new lender via POST /lender/registration
   */
  async register(formData: LenderRegistrationFormData, language: string = 'en'): Promise<LenderRegistrationResponse> {
    const payload = toRegistrationPayload(formData, language);
    const response = await apiClient.post<LenderRegistrationResponse>(
      '/lender/registration',
      payload,
    );
    return response.data;
  },
};

export { handleApiError };
